import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLogin, NGIcon, UKFlag, USFlag } from "asset/icons";
import { FaChevronDown } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
    DocumentText,
    DocumentText1,
    Global,
    Keyboard,
    Note1,
    Personalcard,
} from "iconsax-reactjs";
import { Car } from "lucide-react";

const documents = [
    {
        id: 1,
        title: "Valid IDs",
        items: [
            { icon: <Global color="#6F739B" className="h-5 w-5" />, label: "Passport" },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "NIN Slip",
                flag: <NGIcon className="h-5 w-5" />,
            },
            { icon: <Personalcard color="#6F739B" className="h-5 w-5" />, label: "National ID" },
            { icon: <Car color="#6F739B" className="h-5 w-5" />, label: "Driver’s Licence" },
            {
                icon: <Keyboard className="h-5 w-5" />,
                label: "Voter’s card",
                flag: <NGIcon color="#6F739B" className="h-5 w-5" />,
            },
        ],
    },
    {
        id: 2,
        title: "Proof of address",
        items: [
            { icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />, label: "Utility bill" },
            {
                icon: <Note1 color="#6F739B" className="h-5 w-5" />,
                label: "Bank statement",
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Council tax bill",
                flag: <UKFlag />,
            },
            {
                icon: <DocumentText color="#6F739B" className="h-5 w-5" />,
                label: "Tenancy or Lease agreement",
            },
        ],
    },
    {
        id: 3,
        title: "Business registration documents",
        items: [
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Certificate of Incorporation",
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Salary slips",
                flag: <USFlag />,
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Memorandum and Articles of Association",
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "CAC Status Report, BN 1 Form or CAC Forms 1-7",
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Board resolution to open an account with Tradevu",
                flag: (
                    <span className="flex items-center gap-1">
                        <UKFlag className="h-5 w-5" />
                        <USFlag className="h-5 w-5" />
                    </span>
                ),
            },
            {
                icon: <DocumentText1 color="#6F739B" className="h-5 w-5" />,
                label: "Application letter to open an account with Tradevu",
                flag: <NGIcon className="h-5 w-5" />,
            },
        ],
    },
];

const ActivateAccount = () => {
    const [open, setOpen] = useState(null);
    const navigate = useNavigate();
    const { users: user } = useSelector((s) => s);
    const userName = user.firstName;
    const toggle = (id) => {
        setOpen((prev) => (prev === id ? null : id));
    };

    return (
        <div className="w-full max-w-[700px] mb-5">
            {/* Top Icon */}
            <div className="flex justify-center">
                <AuthLogin className="h-32 w-32" />
            </div>

            {/* Title and Subtitle */}
            <h3 className="text-center text-xl font-thin text-[#475467]">
                Welcome to Tradevu, {userName}!
            </h3>
            <h1 className="text-center text-4xl font-medium text-[#101828] my-2">
                Activate your account
            </h1>
            <p className="text-center font-thin text-normal text-[#475467]">
                Add your business details to get the full experience.
                <br />
                Feel free to do this at anytime.
            </p>

            {/* Accordion */}
            <div className=" rounded-xl bg-white !p-7 mt-5">
                <p className="text-normal text-[#475467]">
                    See a list of documents you might need.
                </p>

                {documents.map((item) => (
                    <div key={item.id} className="">
                        <button
                            type="button"
                            onClick={() => toggle(item.id)}
                            className="flex justify-between items-center w-full py-3 text-left"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-medium border rounded-full h-9 w-9 flex items-center justify-center">
                                    {item.id}
                                </span>
                                <span className="!text-lg  text-[#101828]">{item.title}</span>
                            </div>
                            <FaChevronDown
                                className={`h-4 w-4 text-[#667085] transform transition-transform duration-300 ${
                                    open === item.id ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                        {open === item.id && (
                            <div className="pl-14 pr-4 pb-4">
                                {item.items ? (
                                    <div className="bg-[#F9FAFB] p-4 rounded-xl space-y-4">
                                        {item.items.map((sub, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <div className="h-9 w-9 bg-[#EAECF0] rounded-full flex items-center justify-center">
                                                    {sub.icon}
                                                </div>
                                                <p className="text-[#101828] font-medium flex items-center gap-2">
                                                    {sub.label}
                                                    {sub.flag && sub.flag}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-[#667085] mt-2">
                                        {item.description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {/* Privacy Policy Note */}
                <div className="flex items-start gap-2 text-sm text-[#475467] mt-6">
                    <input type="checkbox" checked readOnly className="accent-[#6F00FF] mt-1" />
                    <span>
                        We’re committed to protecting your privacy. Your business info will stay
                        confidential, used for verification only, in line with our{" "}
                        <span className="text-[#6F00FF] font-medium underline">Privacy Policy</span>
                        .
                    </span>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 items-center mt-6">
                    <button
                        type="button"
                        className="!text-lg text-[#101828] hover:underline"
                        onClick={() => navigate("/dashboard")}
                    >
                        Skip for now
                    </button>
                    <button
                        type="button"
                        className="bg-[#6F00FF] hover:bg-[#5a00d1] text-lg text-white !px-7 !py-3 rounded-lg"
                        onClick={() => navigate("/onboarding/business-details")}
                    >
                        Continue →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ActivateAccount;
